// SEA's and PEA's budgets on the Budgets tab (SPEC §8.6): a vessel's budget as its company database's
// budget inquiry has it, in Sertica's columns, so its rows join the vessel's sheet and its totals.
import { useQuery } from "@tanstack/react-query";
import { formatBudgetPeriod, type PreviousFigures } from "@po/shared";
import { api, type AutoCountSheet, type BudgetsPage } from "../lib/api";

export type AutoCountVessel = BudgetsPage["autoCount"]["vessels"][number];
export type AutoCountLine = AutoCountSheet["lines"][number];

export const autoCountKey = (v: { source: string; database: string; projectNo: string }) => `${v.source}|${v.database}|${v.projectNo}`;

type Raw = Pick<AutoCountVessel, "netBudgetUsd" | "actualUsd" | "reserveUsd" | "balanceUsd">;

/**
 * SEA's figures in Sertica's columns: Budget is the total budget less transfers out, Actual its
 * actual, Other cost its reserve (POs not transferred yet), so Remaining is its balance.
 */
export const sheetFigures = (f: Raw) => ({ budgetUsd: f.netBudgetUsd, committedUsd: f.actualUsd, accruedUsd: 0, otherCostUsd: f.reserveUsd, remainingUsd: f.balanceUsd });

export const previousFigures = (p: AutoCountVessel["previous"]): PreviousFigures | null => (p ? { budgetUsd: p.netBudgetUsd, actualUsd: p.actualUsd } : null);

/** A line with nothing in the period, or SEA's placeholder (1.00 of budget a quarter, nothing else). */
export const isZeroLine = (l: AutoCountLine) => l.placeholder || [l.netBudgetUsd, l.actualUsd, l.reserveUsd, l.transferInUsd, l.transferOutUsd].every((n) => Math.round(n * 100) === 0);

export const sumLines = (ls: Raw[]): Raw => ({
  netBudgetUsd: ls.reduce((n, l) => n + l.netBudgetUsd, 0),
  actualUsd: ls.reduce((n, l) => n + l.actualUsd, 0),
  reserveUsd: ls.reduce((n, l) => n + l.reserveUsd, 0),
  balanceUsd: ls.reduce((n, l) => n + l.balanceUsd, 0),
});

export const sumPrevious = (ps: AutoCountVessel["previous"][]): PreviousFigures | null => {
  const there = ps.filter((p) => p !== null);
  return there.length ? { budgetUsd: there.reduce((n, p) => n + p.netBudgetUsd, 0), actualUsd: there.reduce((n, p) => n + p.actualUsd, 0) } : null;
};

/** A vessel's SEA or PEA lines in the period. */
export function useAutoCountSheet(vessel: AutoCountVessel | undefined, period: BudgetsPage["period"]) {
  const at = formatBudgetPeriod(period);
  return useQuery({
    queryKey: ["budgets/autocount", vessel ? autoCountKey(vessel) : null, at],
    // Plain parameters: `source` and `project` are filter names to qs.
    queryFn: () => api<AutoCountSheet>(`/budgets/autocount?${new URLSearchParams({ source: vessel!.source, database: vessel!.database, project: vessel!.projectNo, period: at })}`),
    enabled: !!vessel,
  });
}
